import React, { ChangeEvent, useState } from "react";
import SearchableSelect from "../../../core/common/SearchableSelect";
import handleClearError from "../../../core/methods/handleClearError";
import { NewInviteType } from "../../../types/types";
import { ErrorType } from "../../../types/common-type";
import { genderOption } from "../../../core/data/js/common-data";
import InputField from "../../../core/common/form-fields/InputField";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import { all_routes } from "../../router/all_routes";
import BreadCrumbForm from "../../../core/common/BreadCrumbForm";
import Footer from "../../../core/common/Footer";
import {
  addNewInviteSchema,
  Validation_addNewInvite,
} from "../../../core/validations/Validation_addNewInvite";

const accountOption = [
  { label: "Savings", value: "Savings" },
  { label: "Current", value: "Current" },
  { label: "NRI", value: "NRI" },
];

const initial_addNewData: NewInviteType = {
  first_name: "",
  middle_name: "",
  last_name: "",
  date_of_birth: "",
  gender: "",
  father_name: "",
  applicant_designation: "",
  applicant_department: "",
  applicant_joining_date: "",
  recruiter_id: "",
  recruiter_name: "",
  upload_url: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pin_code: "",
  country: "",
  period_from: "",
  period_to: "",
  type_of_account: "",
};

const AddNewInviteForm = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<ErrorType>({});
  const [formData, setFormData] = useState<NewInviteType>(initial_addNewData); 
  const handleFieldDataChange = (
    name: string,
    value: string,
    label: string,
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      [name]: prev[name] == value ? "" : value,
    }));
    handleClearError(setErrors, name);
  };
 
  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    handleClearError(setErrors, name);
  };
 
  const handleDateChange = <K extends keyof NewInviteType>(
    field: K,
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };
 
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = Validation_addNewInvite({
      data: formData,
      schema: addNewInviteSchema,
      setErrors,
    }); 
    if (!isValid) return;

    try { 
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Form submitted successfully!",
        confirmButtonText: "OK",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate(all_routes.placeOrder, { replace: true });
        }
      });
    } catch (error) {
      console.error("error:", error);
    }
  };

  const sharedProps = {
    data: formData,
    onChange: handleChange,
    dateChange: handleDateChange,
    errors,
  };
 
  return (
    <div className="page-wrapper">
      <div className="content px-2 px-md-4"> 
        <BreadCrumbForm
          title="Add New Invite"
          prevTitle="Place Order"
          prevLink={all_routes.createNewOrder}
          backLink={all_routes.createNewOrder}
        /> 
        <div className="card p-3 py-4">
          <form onSubmit={handleFormSubmit}>
            <div className="row mb-2 gx-2">
              <InputField<NewInviteType>
                {...sharedProps}
                label="First Name"
                name="first_name"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Middle Name"
                name="middle_name"
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Last Name"
                name="last_name"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Father's Name"
                name="father_name"
                required
              />
 
              <div className="col-6 col-md-4">
                <SearchableSelect
                  label="Gender"
                  name="gender"
                  value={formData?.gender}
                  options={genderOption}
                  onChange={handleFieldDataChange}
                  hasRowGrid={false}
                  error={errors?.gender}
                  required
                />
              </div>
            </div>
 
            <div className="row mb-2 gx-2">
              <InputField<NewInviteType>
                {...sharedProps}
                label="Designation"
                name="applicant_designation"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Department"
                name="applicant_department"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Joining Date"
                name="applicant_joining_date"
                type="date"
                required
              />
            </div>

            {/* ── Recruiter Information ───────────────────────────── */}
            <div className="row mb-2 gx-2">
              <InputField<NewInviteType>
                {...sharedProps}
                label="Recruiter ID"
                name="recruiter_id"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Recruiter Name"
                name="recruiter_name"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Upload URL"
                name="upload_url"
                type="url"
                placeholder="https://"
                colClass="col-sm-6 col-md-4"
                required
              />
            </div>

            {/* ── Address ─────────────────────────────────────────── */}
            <div className="row mb-2 gx-2">
              <InputField<NewInviteType>
                {...sharedProps}
                label="Address Line 1"
                name="address_line1"
                colClass="col-sm-12"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Address Line 2"
                name="address_line2"
                colClass="col-sm-12"
                placeholder="Apt, suite, floor (optional)"
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="City"
                name="city"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="State"
                name="state"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Pin Code"
                name="pin_code"
                inputMode="numeric"
                maxLength={6}
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Country"
                name="country"
                required
              />

              {/* Account Type — native select */}
              <div className="col-sm-6 col-md-4">
                <SearchableSelect
                  label="Type of Account"
                  name="type_of_account"
                  value={formData?.type_of_account}
                  options={accountOption}
                  onChange={handleFieldDataChange}
                  hasRowGrid={false}
                  error={errors?.type_of_account}
                  required
                />
              </div>
            </div>

            {/* ── Period & Account ────────────────────────────────── */}
            <div className="row mb-2 gx-2">
              <InputField<NewInviteType>
                {...sharedProps}
                label="Period From"
                name="period_from"
                type="date"
                required
              />
              <InputField<NewInviteType>
                {...sharedProps}
                label="Period To"
                name="period_to"
                type="date"
                required
              />
            </div>
            <div className="text-end">
              <button type="submit" className="btn btn-primary px-4 fs-16">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ---- Footer ---- */}
      <Footer />
    </div>
  );
};

export default AddNewInviteForm;
